/**
 * `<pg-button>` — botão reutilizável do Design System.
 *
 * Atributos:
 * - `variant`: 'primary' (padrão) | 'secondary' | 'ghost' | 'danger'
 * - `disabled`: presença desabilita o botão
 * - `loading`: presença mostra estado de carregamento (e desabilita clique)
 *
 * Uso:
 * ```html
 * <pg-button variant="primary">Salvar</pg-button>
 * ```
 *
 * Evento emitido: `pg-click` (CustomEvent, bubbles+composed) — a tela
 * que consome o componente escuta esse evento, nunca um callback global.
 * Este componente nunca conhece o que "Salvar" significa em termos de
 * negócio — apenas renderiza e emite o clique.
 */
export class PgButton extends HTMLElement {
    static get observedAttributes() {
        return ['variant', 'disabled', 'loading'];
    }
    root;
    constructor() {
        super();
        this.root = this.attachShadow({ mode: 'open' });
    }
    connectedCallback() {
        this.render();
    }
    attributeChangedCallback() {
        this.render();
    }
    render() {
        const variant = this.getAttribute('variant') ?? 'primary';
        const desabilitado = this.hasAttribute('disabled') || this.hasAttribute('loading');
        const carregando = this.hasAttribute('loading');
        this.root.innerHTML = `
      <style>
        :host {
          display: inline-block;
        }
        button {
          font-family: var(--pg-font-familia-base, system-ui);
          font-size: var(--pg-font-size-sm, 0.875rem);
          font-weight: var(--pg-font-weight-medio, 500);
          padding: var(--pg-space-3, 12px) var(--pg-space-5, 24px);
          border-radius: var(--pg-radius-md, 10px);
          border: var(--pg-border-largura, 1px) solid transparent;
          cursor: pointer;
          transition: background-color 0.15s ease, opacity 0.15s ease;
          display: inline-flex;
          align-items: center;
          gap: var(--pg-space-2, 8px);
        }
        button:focus-visible {
          outline: none;
          box-shadow: var(--pg-shadow-foco, 0 0 0 3px rgba(47,140,255,0.35));
        }
        button:disabled {
          cursor: not-allowed;
          opacity: 0.55;
        }
        .primary {
          background: var(--pg-color-destaque, #2f8cff);
          color: #ffffff;
        }
        .primary:not(:disabled):hover {
          background: var(--pg-color-destaque-hover, #5aa3ff);
        }
        .secondary {
          background: var(--pg-color-bg-elevado, #1a1d21);
          color: var(--pg-color-texto-primario, #f2f3f5);
          border-color: var(--pg-color-borda, #2a2f36);
        }
        .secondary:not(:disabled):hover {
          background: var(--pg-color-bg-hover, #22262b);
        }
        .ghost {
          background: transparent;
          color: var(--pg-color-texto-primario, #f2f3f5);
        }
        .ghost:not(:disabled):hover {
          background: var(--pg-color-bg-hover, #22262b);
        }
        .danger {
          background: var(--pg-color-erro, #e5484d);
          color: #ffffff;
        }
        .spinner {
          width: 12px;
          height: 12px;
          border: 2px solid rgba(255, 255, 255, 0.4);
          border-top-color: #ffffff;
          border-radius: 50%;
          animation: girar 0.6s linear infinite;
        }
        @keyframes girar {
          to { transform: rotate(360deg); }
        }
      </style>
      <button part="button" class="${variant}" ${desabilitado ? 'disabled' : ''}>
        ${carregando ? '<span class="spinner" aria-hidden="true"></span>' : ''}
        <slot></slot>
      </button>
    `;
        this.root.querySelector('button')?.addEventListener('click', (evento) => {
            if (desabilitado) {
                evento.stopImmediatePropagation();
                return;
            }
            this.dispatchEvent(new CustomEvent('pg-click', { bubbles: true, composed: true }));
        });
    }
}
customElements.define('pg-button', PgButton);
//# sourceMappingURL=pg-button.js.map